import { useMutation, useQueryClient } from "react-query";
import { updateUserStatusAsync } from "../../../api/customers";
import { useGlobalToast } from "../../../components/GlobalToastProvider";

interface UpdateWorkflowStatusPayload {
  clientId: string;
  status: string;
}

interface UseUpdateClientWorkflowStatusOptions {
  onSuccess?: (data: any) => void;
  onError?: (error: unknown) => void;
}

/**
 * Custom hook for updating client workflow status
 * Uses the /roadmap/{clientId}/change-status endpoint via updateUserStatusAsync API function
 *
 * @example
 * const { updateWorkflowStatus, isUpdating } = useUpdateClientWorkflowStatus({
 *   onSuccess: () => {
 *     showToast({ message: "Status updated!" });
 *   }
 * });
 *
 * updateWorkflowStatus({ clientId: "123", status: "FULL_CUSTOMER" });
 */
export const useUpdateClientWorkflowStatus = (
  options: UseUpdateClientWorkflowStatusOptions = {}
) => {
  const { onSuccess, onError } = options;
  const { showToast } = useGlobalToast();
  const queryClient = useQueryClient();

  const { mutate, isLoading, ...rest } = useMutation(
    updateUserStatusAsync,
    {
      onSuccess: (data) => {
        showToast({
          message: "Client status updated successfully",
        });

        // Invalidate customer details queries to refresh data
        // The useCustomerDetails hook uses ["customer", clientId] as the query key
        queryClient.invalidateQueries(["customer"]);
        queryClient.invalidateQueries(["customerDetails"]);
        queryClient.invalidateQueries(["getHasClientAccountAccess"]);

        onSuccess?.(data);
      },
      onError: (error: unknown) => {
        console.error("Error updating workflow status:", error);
        showToast({
          message: "Failed to update client status",
          errorState: true,
        });
        onError?.(error);
      },
    }
  );

  return {
    updateWorkflowStatus: mutate,
    isUpdating: isLoading,
    ...rest,
  };
};

export default useUpdateClientWorkflowStatus;
